import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
export default function TheDermaCoralStandard() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="46680" id="our-story" ref={ref} className="bg-background py-24 lg:py-32 relative overflow-hidden border-b border-muted/50">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left Column - Decorative Number */}
          <div className={cn("lg:col-span-4 flex flex-col justify-start items-start lg:items-end transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10")}>
            <span className="font-serif text-[8rem] md:text-[12rem] lg:text-[14rem] leading-[0.8] text-primary/5 select-none tracking-tighter">
              01
            </span>
          </div>

          {/* Right Column - Narrative Content */}
          <div className={cn("lg:col-span-8 relative space-y-8 transition-all duration-1000 delay-300 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
            <div className="prose prose-lg prose-stone max-w-none text-muted-foreground font-sans">
              <h2 className="text-3xl md:text-5xl font-serif text-foreground mb-8 tracking-tight">
                The Bodywerks Philosophy: Structural Harmony
              </h2>
              
              <p className="leading-relaxed text-lg">
                At Bodywerks, we believe that true physical freedom is found in the balance between structural integrity and functional movement. Our philosophy is rooted in the understanding that the body is a self-healing organism, provided the nervous system is free from interference. We have created a clinical environment in Cyprus that honors this complexity, moving away from the cold, impersonal nature of traditional healthcare.
              </p>

              <div className="my-10 flex items-center gap-6">
                <div className="relative size-24 shrink-0 overflow-hidden rounded-full border border-border shadow-sm">
                   <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/coral_texture_detail_01.png" alt="Microscopic coral texture detail" className="size-full object-cover" />
                </div>
                <p className="text-base italic text-muted-foreground/90 border-l-2 border-accent/40 pl-4 font-serif">
                  "We treat the source, not just the symptom, ensuring your body performs at its highest potential through precise alignment."
                </p>
              </div>

              <p className="leading-relaxed text-lg">
                Our approach is uniquely personalized. Unlike high-volume clinics that rely on standardized templates, our specialists spend the time necessary to understand your history, your lifestyle, and your specific biomechanical goals. We utilize evidence-based chiropractic techniques combined with modern rehabilitative science to create a bespoke roadmap for your recovery. This dedication to precision ensures results that are both immediate and sustainable.
              </p>

              <div className="float-right ml-8 mb-6 mt-2 relative size-32 md:size-40 overflow-hidden rounded-full border-4 border-background shadow-xl">
                 <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1609030/img/coral_accent_interior_02.png" alt="Soft coral interior accent" className="size-full object-cover" />
              </div>

              <p className="leading-relaxed text-lg">
                Clinical excellence and safety are the cornerstones of the Bodywerks Standard. While our clinic exudes the tranquility of a modern sanctuary—featuring a minimalist palette of limestone and slate—our operational protocols are strictly evidence-based. We utilize advanced diagnostic tools and gentle adjustment methods, ensuring that your journey to wellness is as safe as it is transformative. You are cared for by experts who prioritize your mobility above all else.
              </p>
              
              <p className="leading-relaxed text-lg">
                Experience the difference of a practice that values your long-term health as much as your immediate relief. At Bodywerks, we don't just adjust the spine; we elevate your quality of life, offering a sanctuary where chiropractic science meets compassionate care.
              </p>
            </div>

            <div className="pt-8">
              <Button asChild size="lg" className="group relative overflow-hidden bg-primary text-primary-foreground transition-all duration-500 hover:shadow-xl hover:opacity-90 border border-transparent rounded-full px-8">
                <Link to="/TreatmentsPage">
                  <span className="relative z-10 flex items-center gap-2 font-sans tracking-wide">
                    Explore Treatments
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}